import React, { useState, useEffect, useRef, useCallback } from 'react';
import './LiveChat.css';

const CHAT_STORAGE_KEY = 'cod_chat_history';
const MAX_STORED_MESSAGES = 20;

// Helper to get auth token
const getAuthToken = () => {
  return localStorage.getItem('token');
};

// Helper to save chat history to localStorage
const saveChatHistory = (chatMessages) => {
  try {
    const storageData = {};
    chatMessages.forEach((messages, odId) => {
      // Only keep last 20 messages per conversation
      storageData[odId] = messages.slice(-MAX_STORED_MESSAGES);
    });
    localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(storageData));
  } catch (error) {
    console.error('Failed to save chat history:', error);
  }
};

// Helper to load chat history from localStorage
const loadChatHistory = () => {
  try {
    const stored = localStorage.getItem(CHAT_STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      const map = new Map();
      Object.keys(data).forEach(odId => {
        map.set(odId, data[odId]);
      });
      return map;
    }
  } catch (error) {
    console.error('Failed to load chat history:', error);
  }
  return new Map();
};

// Helper to get glow class - role-based glows override subscription glows
const getGlowClassForUser = (role, subscriptionTier) => {
  // Role-based glows take priority (manager = fire, main_admin = water)
  if (role === 'manager') return 'glow-manager';
  if (role === 'main_admin') return 'glow-main_admin';

  // Fall back to subscription tier glow
  if (!subscriptionTier) return '';
  const glowClasses = {
    'basic': 'glow-basic',
    'standard': 'glow-standard',
    'plus': 'glow-plus',
    'premium': 'glow-premium',
    'max': 'glow-max'
  };
  return glowClasses[subscriptionTier] || '';
};

const LiveChat = ({
  currentUser,
  onlineUsers = [],
  socket,
  isMobileChatActive = false,
  onCloseMobileChat,
  onUnreadCountUpdate,
  t
}) => {
  const [isMinimized, setIsMinimized] = useState(true);
  const [activeChats, setActiveChats] = useState(() => {
    try {
      const saved = localStorage.getItem('duelity_active_chats');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [typingUsers, setTypingUsers] = useState(new Set());
  const [chatMessages, setChatMessages] = useState(() => loadChatHistory());
  const [localOnlineUsers, setLocalOnlineUsers] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState(new Map());
  const [isMobile, setIsMobile] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(new Set());
  const messagesEndRef = useRef(null);

  // Save to localStorage whenever chatMessages changes
  useEffect(() => {
    saveChatHistory(chatMessages);
  }, [chatMessages]);

  // Save active chats to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('duelity_active_chats', JSON.stringify(activeChats));
    } catch (e) {
      console.error('Failed to save active chats:', e);
    }
  }, [activeChats]);

  // Update chat user data (role, subscription, online status) from online users
  useEffect(() => {
    // Always run this effect to update online/offline status in real-time
    setActiveChats(prev => {
      if (prev.length === 0) return prev;
      
      return prev.map(chat => {
        const onlineUser = localOnlineUsers.find(u => u._id === chat.user.id);
        if (onlineUser) {
          return {
            ...chat,
            user: {
              ...chat.user,
              role: onlineUser.role || chat.user.role,
              subscriptionTier: onlineUser.subscriptionTier || chat.user.subscriptionTier,
              isOnline: true
            }
          };
        } else {
          // User is offline but keep their stored role/subscription
          return {
            ...chat,
            user: {
              ...chat.user,
              isOnline: false
            }
          };
        }
      });
    });
  }, [localOnlineUsers]);

  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Fetch chat history from server
  const fetchChatHistory = useCallback(async (userId) => {
    if (loadingHistory.has(userId)) return;

    setLoadingHistory(prev => new Set([...prev, userId]));

    try {
      const token = getAuthToken();
      const response = await fetch(`/api/messages/${userId}?limit=20`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const messages = await response.json();
        const currentUserId = (currentUser.id || currentUser._id || '').toString();

        // Convert server messages to local format
        const formattedMessages = messages.reverse().map(msg => ({
          id: msg._id,
          text: msg.message,
          sender: {
            id: msg.from._id,
            name: msg.from.inGameName || msg.from.username
          },
          timestamp: new Date(msg.createdAt),
          isOwn: msg.from._id.toString() === currentUserId
        }));

        // Merge with existing messages, avoiding duplicates
        setChatMessages(prev => {
          const existingMessages = prev.get(userId) || [];
          const existingIds = new Set(existingMessages.map(m => m.id));
          const newMessages = formattedMessages.filter(m => !existingIds.has(m.id));

          // Combine and sort by timestamp
          const combined = [...newMessages, ...existingMessages]
            .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
            .slice(-MAX_STORED_MESSAGES);

          const updatedMessages = new Map(prev);
          updatedMessages.set(userId, combined);
          return updatedMessages;
        });

        // Mark messages as read
        fetch(`/api/messages/read/${userId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }).catch(() => {});
      }
    } catch (error) {
      console.error('Failed to fetch chat history:', error);
    } finally {
      setLoadingHistory(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  }, [currentUser, loadingHistory]);

  // Enhanced filtering and syncing of online users
  useEffect(() => {
    if (!currentUser) {
      setLocalOnlineUsers([]);
      return;
    }

    // Filter out current user and any invalid users
    const filteredUsers = onlineUsers.filter(user => {
      if (!user || !user._id) {
        return false;
      }

      const odId = user._id.toString();
      const currentId = (currentUser.id || currentUser._id || '').toString();

      // Filter out current user and banned users
      const isCurrentUser = odId === currentId;
      const isBanned = user.banned === true;

      return !isCurrentUser && !isBanned;
    });

    setLocalOnlineUsers(filteredUsers);
  }, [onlineUsers, currentUser]);

  // Calculate total unread messages and notify parent
  const getTotalUnreadCount = () => {
    let total = 0;
    unreadCounts.forEach(count => total += count);
    return total;
  };

  // Notify parent component of unread count changes
  useEffect(() => {
    if (onUnreadCountUpdate) {
      const totalUnread = getTotalUnreadCount();
      onUnreadCountUpdate(totalUnread);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadCounts, onUnreadCountUpdate]);

  // Enhanced socket event handling
  useEffect(() => {
    if (!socket || !currentUser) {
      return;
    }

    const handleNewMessage = (messageData) => {
      const senderId = messageData.from._id;
      const senderName = messageData.from.inGameName || messageData.from.username;

      // Add message to the appropriate chat
      setChatMessages(prev => {
        const userMessages = prev.get(senderId) || [];
        const newMessage = {
          id: messageData._id,
          text: messageData.message,
          sender: { id: senderId, name: senderName },
          timestamp: new Date(messageData.createdAt),
          isOwn: false
        };

        const updatedMessages = new Map(prev);
        const combined = [...userMessages, newMessage].slice(-MAX_STORED_MESSAGES);
        updatedMessages.set(senderId, combined);
        return updatedMessages;
      });

      // Update unread count
      setUnreadCounts(prev => {
        const existingChat = activeChats.find(chat => chat.id === senderId);
        const isMinimizedChat = existingChat?.isMinimized;
        const isMobileChatClosed = isMobile && !isMobileChatActive;

        if (isMinimizedChat || isMobileChatClosed) {
          const currentCount = prev.get(senderId) || 0;
          const updatedCounts = new Map(prev);
          updatedCounts.set(senderId, currentCount + 1);
          return updatedCounts;
        }
        return prev;
      });

      // Auto-open chat if not already open
      setActiveChats(prev => {
        const existingChat = prev.find(chat => chat.id === senderId);
        if (!existingChat) {
          const sender = localOnlineUsers.find(u => u._id === senderId);
          if (sender) {
            const isMinimizedOnMobile = isMobile && !isMobileChatActive;
            return [...prev, {
              id: senderId,
              user: {
                id: senderId,
                name: senderName,
                avatar: sender.avatar || '/api/placeholder/40/40',
                status: 'online'
              },
              isMinimized: isMinimizedOnMobile,
              unreadCount: isMinimizedOnMobile ? 1 : 0
            }];
          }
        } else {
          // Update unread count if chat is minimized
          return prev.map(chat => {
            if (chat.id === senderId) {
              const shouldIncrement = chat.isMinimized || (isMobile && !isMobileChatActive);
              return {
                ...chat,
                unreadCount: shouldIncrement ? (chat.unreadCount || 0) + 1 : chat.unreadCount
              };
            }
            return chat;
          });
        }
        return prev;
      });

      // Show browser notification if mobile chat is closed
      if (isMobile && !isMobileChatActive && 'Notification' in window && Notification.permission === 'granted') {
        new Notification(`${t('newMessage')} ${t('from')} ${senderName}`, {
          body: messageData.message,
          icon: '/favicon.ico',
          tag: 'cod-chat'
        });
      }
    };

    const handleMessageSent = (messageData) => {
      // Message sent confirmation handling
    };

    const handleUserTyping = (data) => {
      setTypingUsers(prev => new Set([...prev, data.from]));
    };

    const handleUserStoppedTyping = (data) => {
      setTypingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(data.from);
        return newSet;
      });
    };

    // Enhanced real-time online user updates
    const handleUserOnline = (userData) => {
      if (!userData || !userData.userId) {
        return;
      }

      const currentId = (currentUser.id || currentUser._id || '').toString();
      const newUserId = userData.userId.toString();

      // Don't add current user to the list
      if (newUserId === currentId) {
        return;
      }

      setLocalOnlineUsers(prev => {
        // Remove user if already exists to avoid duplicates
        const filtered = prev.filter(u => u._id?.toString() !== newUserId);

        // Add the user with updated online status (including subscription tier)
        const newUser = {
          _id: userData.userId,
          username: userData.username,
          inGameName: userData.inGameName,
          points: userData.points,
          role: userData.role,
          banned: userData.banned,
          isAdmin: userData.isAdmin,
          canApprove: userData.canApprove,
          subscriptionTier: userData.subscriptionTier,
          level: userData.level,
          currentTitle: userData.currentTitle,
          badges: userData.badges
        };

        return [...filtered, newUser];
      });

      // Immediately update any open chat windows for this user
      setActiveChats(prev => prev.map(chat => {
        if (chat.user.id === newUserId) {
          return {
            ...chat,
            user: {
              ...chat.user,
              role: userData.role || chat.user.role,
              subscriptionTier: userData.subscriptionTier || chat.user.subscriptionTier,
              isOnline: true
            }
          };
        }
        return chat;
      }));
    };

    const handleUserOffline = (data) => {
      if (!data || !data.userId) {
        return;
      }

      const offlineUserId = data.userId.toString();

      // Remove from online users list
      setLocalOnlineUsers(prev => {
        const filtered = prev.filter(u => u._id?.toString() !== offlineUserId);
        return filtered;
      });

      // Immediately update any open chat windows for this user
      setActiveChats(prev => prev.map(chat => {
        if (chat.user.id === offlineUserId) {
          return {
            ...chat,
            user: {
              ...chat.user,
              isOnline: false
            }
          };
        }
        return chat;
      }));
    };

    const handleOnlineUsers = (users) => {
      if (!Array.isArray(users)) {
        return;
      }

      const currentId = (currentUser.id || currentUser._id || '').toString();

      // Filter out current user and invalid users
      const validUsers = users.filter(user => {
        if (!user || !user._id) return false;
        const odId = user._id.toString();
        return odId !== currentId && !user.banned;
      });

      setLocalOnlineUsers(validUsers);
    };

    const handleError = (error) => {
      // Handle chat errors silently or with user-friendly messages
    };

    // Register socket event listeners
    socket.on('newMessage', handleNewMessage);
    socket.on('messageSent', handleMessageSent);
    socket.on('userTyping', handleUserTyping);
    socket.on('userStoppedTyping', handleUserStoppedTyping);
    socket.on('userOnline', handleUserOnline);
    socket.on('userOffline', handleUserOffline);
    socket.on('onlineUsers', handleOnlineUsers);
    socket.on('error', handleError);

    return () => {
      // Cleanup socket listeners
      socket.off('newMessage', handleNewMessage);
      socket.off('messageSent', handleMessageSent);
      socket.off('userTyping', handleUserTyping);
      socket.off('userStoppedTyping', handleUserStoppedTyping);
      socket.off('userOnline', handleUserOnline);
      socket.off('userOffline', handleUserOffline);
      socket.off('onlineUsers', handleOnlineUsers);
      socket.off('error', handleError);
    };
  }, [socket, currentUser, activeChats, isMobile, isMobileChatActive, localOnlineUsers, t]);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Clear unread counts when mobile chat becomes active
  useEffect(() => {
    if (isMobileChatActive && isMobile) {
      setUnreadCounts(new Map());
    }
  }, [isMobileChatActive, isMobile]);

  const openChat = (user) => {
    if (!activeChats.find(chat => chat.id === user._id)) {
      const newChat = {
        id: user._id,
        user: {
          id: user._id,
          name: user.inGameName || user.username,
          avatar: user.avatar || '/api/placeholder/40/40',
          role: user.role,
          subscriptionTier: user.subscriptionTier,
          isOnline: true
        },
        isMinimized: false,
        unreadCount: 0
      };
      setActiveChats(prev => [...prev, newChat]);

      // Fetch chat history for this user
      fetchChatHistory(user._id);

      // Clear unread count for this user
      setUnreadCounts(prev => {
        const updated = new Map(prev);
        updated.delete(user._id);
        return updated;
      });
    }

    // On mobile, close the chat list after opening a chat
    if (isMobile && onCloseMobileChat) {
      onCloseMobileChat();
    }
  };

  const closeChat = (userId) => {
    setActiveChats(prev => prev.filter(chat => chat.id !== userId));
    // Don't clear messages - keep them in localStorage for persistence
    // Clear unread count for this user
    setUnreadCounts(prev => {
      const updated = new Map(prev);
      updated.delete(userId);
      return updated;
    });
  };

  const minimizeChat = (userId) => {
    setActiveChats(prev =>
      prev.map(chat =>
        chat.id === userId
          ? { ...chat, isMinimized: !chat.isMinimized, unreadCount: 0 }
          : chat
      )
    );

    // Clear unread count when maximizing chat
    setUnreadCounts(prev => {
      const chat = activeChats.find(c => c.id === userId);
      if (chat?.isMinimized) { // If we're maximizing
        const updated = new Map(prev);
        updated.delete(userId);
        return updated;
      }
      return prev;
    });
  };

  const sendMessage = (userId, message) => {
    if (socket && socket.connected) {
      // Send message using your existing socket event
      socket.emit('sendMessage', {
        to: userId,
        message: message
      });

      // Add message to local state immediately for instant feedback
      setChatMessages(prev => {
        const userMessages = prev.get(userId) || [];
        const newMessage = {
          id: Date.now() + Math.random(),
          text: message,
          sender: currentUser,
          timestamp: new Date(),
          isOwn: true
        };

        const updatedMessages = new Map(prev);
        const combined = [...userMessages, newMessage].slice(-MAX_STORED_MESSAGES);
        updatedMessages.set(userId, combined);
        return updatedMessages;
      });
    }
  };

  const handleTyping = (userId) => {
    if (socket && socket.connected) {
      socket.emit('typing', { to: userId });
    }
  };

  const handleStopTyping = (userId) => {
    if (socket && socket.connected) {
      socket.emit('stopTyping', { to: userId });
    }
  };

  const filteredUsers = localOnlineUsers.filter(user => {
    if (!user || !user._id) return false;

    const searchMatch = (user.inGameName || user.username || '')
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

    return searchMatch;
  });

  const isAdminUser = (user) => {
    return ['main_admin', 'manager', 'supervisor', 'approver', 'verified_sponsor'].includes(user.role);
  };

  const getRoleIcon = (user) => {
    const roleIcons = {
      'main_admin': '👑',    // Crown for Main Admin
      'manager': '⚜️',       // Orange diamond for Manager
      'supervisor': '⭐',    // Star for Supervisor
      'approver': '🏅',      // Medal for Approver
      'verified_sponsor': '✔️' // Diamond for Verified Sponsor
    };
    return roleIcons[user.role] || '';
  };

  // Get subscription tier icon for user list
  const getSubscriptionTierIcon = (tier) => {
    const tierIcons = {
      'basic': '🎖️',
      'standard': '⭐',
      'plus': '➕',
      'premium': '🔥',
      'max': '🔱'
    };
    return tierIcons[tier] || '';
  };

  const getChatUserRole = (chatUser) => {
    // First check if role is stored on the chat user object
    if (chatUser.role) return chatUser.role;
    // Otherwise look up in online users
    const fullUser = localOnlineUsers.find(u => u._id === chatUser.id);
    return fullUser?.role || null;
  };

  // Get subscription tier from chat user or online users
  const getChatUserSubscriptionTier = (chatUser) => {
    if (chatUser.subscriptionTier) return chatUser.subscriptionTier;
    const fullUser = localOnlineUsers.find(u => u._id === chatUser.id);
    return fullUser?.subscriptionTier || null;
  };

  // Get glow class for chat user (role overrides subscription)
  const getChatUserGlowClass = (chatUser) => {
    const role = getChatUserRole(chatUser);
    const tier = getChatUserSubscriptionTier(chatUser);
    return getGlowClassForUser(role, tier);
  };

  const isChatUserAdmin = (chatUser) => {
    const role = getChatUserRole(chatUser);
    return ['main_admin', 'manager', 'supervisor', 'approver', 'verified_sponsor'].includes(role);
  };

  const getChatUserRoleIcon = (chatUser) => {
    const role = getChatUserRole(chatUser);
    const roleIcons = {
      'main_admin': '👑',    // Crown for Main Admin
      'manager': '⚜️',       // Orange diamond for Manager
      'supervisor': '⭐',    // Star for Supervisor
      'approver': '🏅',      // Medal for Approver
      'verified_sponsor': '✔️' // Diamond for Verified Sponsor
    };
    return roleIcons[role] || '';
  };

  if (!currentUser) {
    return null;
  }

  return (
    <>
      {/* Mobile Chat Overlay */}
      <div
        className={`mobile-chat-overlay ${isMobileChatActive ? 'active' : ''}`}
        onClick={onCloseMobileChat}
      />

      {/* Main Chat Sidebar */}
      <div className={`chat-sidebar ${isMinimized ? 'minimized' : ''} ${isMobileChatActive ? 'mobile-active' : ''}`}>
        <div className="chat-header">
          <div className="chat-title">
            <span className="chat-icon">💬</span>
            <h3>{t('chat')} ({localOnlineUsers.length})</h3>
          </div>
          <button
            className="minimize-btn"
            onClick={() => {
              if (isMobile) {
                onCloseMobileChat();
              } else {
                setIsMinimized(!isMinimized);
              }
            }}
            aria-label={isMobile ? t('closeChat') : (isMinimized ? t('expandChat') : t('minimizeChat'))}
          >
            {isMobile ? '✕' : (isMinimized ? '▲' : '▼')}
          </button>
        </div>

        {(!isMinimized || isMobileChatActive) && (
          <div className="chat-content">
            <div className="search-container">
              <input
                type="text"
                placeholder={t('searchPlayers')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="user-search"
              />
            </div>

            <div className="users-list">
              {filteredUsers.length === 0 ? (
                <div className="no-users-message">
                  {localOnlineUsers.length === 0
                    ? t('noPlayersOnline')
                    : t('noPlayersMatch')
                  }
                </div>
              ) : (
                filteredUsers.map(user => {
                  const userUnreadCount = unreadCounts.get(user._id) || 0;

                  return (
                    <div
                      key={user._id}
                      className="user-item"
                      onClick={() => openChat(user)}
                    >
                      <div className="user-info">
                        <div className={`user-name ${isAdminUser(user) ? 'admin-name' : ''}`}>
                          {isAdminUser(user) && <span className="role-badge-list">{getRoleIcon(user)}</span>}
                          <span className={`name-text ${getGlowClassForUser(user.role, user.subscriptionTier)}`}>{user.inGameName || user.username}</span>
                          {user.subscriptionTier && <span className="sub-badge-list">{getSubscriptionTierIcon(user.subscriptionTier)}</span>}
                          {userUnreadCount > 0 && (
                            <span className="unread-badge" style={{
                              position: 'relative',
                              top: 'auto',
                              right: 'auto',
                              marginLeft: '8px',
                              fontSize: '10px',
                              minWidth: '16px',
                              height: '16px'
                            }}>
                              {userUnreadCount}
                            </span>
                          )}
                        </div>
                        <div className="user-status">{t('online')}</div>
                      </div>
                      {typingUsers.has(user._id) && (
                        <div className="typing-indicator">
                          <span>💬</span>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Individual Chat Windows */}
      {activeChats.map((chat, index) => (
        <ChatWindow
          key={chat.id}
          chat={chat}
          position={index}
          onClose={() => closeChat(chat.id)}
          onMinimize={() => minimizeChat(chat.id)}
          onSendMessage={(message) => sendMessage(chat.id, message)}
          onTyping={() => handleTyping(chat.id)}
          onStopTyping={() => handleStopTyping(chat.id)}
          currentUser={currentUser}
          messages={chatMessages.get(chat.id) || []}
          onlineUsers={localOnlineUsers}
          getChatUserRole={getChatUserRole}
          isChatUserAdmin={isChatUserAdmin}
          getChatUserRoleIcon={getChatUserRoleIcon}
          getChatUserSubscriptionTier={getChatUserSubscriptionTier}
          getSubscriptionTierIcon={getSubscriptionTierIcon}
          isMobile={isMobile}
          isLoadingHistory={loadingHistory.has(chat.id)}
          t={t}
        />
      ))}
    </>
  );
};

const ChatWindow = ({
  chat,
  position,
  onClose,
  onMinimize,
  onSendMessage,
  onTyping,
  onStopTyping,
  currentUser,
  messages = [],
  onlineUsers,
  getChatUserRole,
  isChatUserAdmin,
  getChatUserRoleIcon,
  getChatUserSubscriptionTier,
  getSubscriptionTierIcon,
  isMobile,
  isLoadingHistory,
  t
}) => {
  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-focus input when chat is opened or un-minimized (PC only)
  useEffect(() => {
    if (!isMobile && !chat.isMinimized && inputRef.current) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [chat.isMinimized, isMobile]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (message.trim()) {
      onSendMessage(message);
      setMessage('');

      // Stop typing indicator when message is sent
      if (isTyping) {
        setIsTyping(false);
        onStopTyping();
      }
    }
  };

  const handleTyping = (e) => {
    setMessage(e.target.value);

    if (!isTyping && e.target.value.trim()) {
      setIsTyping(true);
      onTyping();
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set new timeout to stop typing indicator
    typingTimeoutRef.current = setTimeout(() => {
      if (isTyping) {
        setIsTyping(false);
        onStopTyping();
      }
    }, 2000);
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMessageSenderRole = (msg) => {
    if (msg.isOwn) {
      return currentUser.role;
    } else {
      // Find the sender in onlineUsers to get their role
      const sender = onlineUsers.find(user => user._id === msg.sender.id);
      return sender?.role || null;
    }
  };

  const getMessageSenderRoleIcon = (msg) => {
    if (msg.isOwn) {
      const roleIcons = {
        'main_admin': '👑',
        'manager': '⚜️',
        'supervisor': '⭐',
        'approver': '🏅',
        'verified_sponsor': '✔️'
      };
      return roleIcons[currentUser.role] || '';
    } else {
      // Find the sender in onlineUsers to get their role
      const sender = onlineUsers.find(user => user._id === msg.sender.id);
      const roleIcons = {
        'main_admin': '👑',
        'manager': '⚜️',
        'supervisor': '⭐',
        'approver': '🏅',
        'verified_sponsor': '✔️'
      };
      return roleIcons[sender?.role] || '';
    }
  };

  const isMessageFromAdmin = (msg) => {
    const role = getMessageSenderRole(msg);
    return ['main_admin', 'manager', 'supervisor', 'approver', 'verified_sponsor'].includes(role);
  };

  // Get subscription tier for message sender
  const getMessageSenderSubscriptionTier = (msg) => {
    if (msg.isOwn) {
      return currentUser.subscriptionTier || null;
    } else {
      const sender = onlineUsers.find(user => user._id === msg.sender.id);
      return sender?.subscriptionTier || null;
    }
  };

  // Get glow class - role-based glows override subscription glows
  // Get glow class for message sender
  const getSubscriptionGlowClass = (msg) => {
    const senderId = msg.from._id || msg.from;
    const isOwnMessage = senderId === (currentUser?.id || currentUser?._id);

    if (isOwnMessage) {
      return getGlowClassForUser(currentUser?.role, currentUser?.subscriptionTier);
    }

    const sender = onlineUsers.find(u => u._id === senderId);
    return getGlowClassForUser(sender?.role, sender?.subscriptionTier);
  };

  // Get own message glow class
  const getOwnMessageGlowClass = () => {
    return getGlowClassForUser(currentUser?.role, currentUser?.subscriptionTier);
  };

  // Get glow class for chat header user
  const getChatHeaderGlowClass = (chatUser) => {
    // First check if chat user has role/tier stored
    if (chatUser.role || chatUser.subscriptionTier) {
      return getGlowClassForUser(chatUser.role, chatUser.subscriptionTier);
    }
    // Otherwise look up in online users
    const fullUser = onlineUsers.find(u => u._id === chatUser.id);
    return getGlowClassForUser(fullUser?.role, fullUser?.subscriptionTier);
  };

  // Updated positioning calculation for wider chat windows (340px + gap)
  const chatWindowWidth = isMobile ? 300 : 340;
  const chatGap = 20; // Gap between chat windows
  const rightOffset = isMobile ? 10 : 20 + (position * (chatWindowWidth + chatGap));

  return (
    <div
      className={`chat-window ${chat.isMinimized ? 'minimized' : ''}`}
      style={{ right: `${rightOffset}px` }}
    >
      <div className="chat-window-header">
        <div className="chat-user-info">
          <div>
            <div className={`chat-user-name ${isChatUserAdmin(chat.user) ? 'admin-name' : ''}`}>
              {isChatUserAdmin(chat.user) && <span className="role-badge-header">{getChatUserRoleIcon(chat.user)}</span>}
              <span className={`name-text ${getChatHeaderGlowClass(chat.user)}`}>{chat.user.name}</span>
              {getChatUserSubscriptionTier(chat.user) && <span className="sub-badge-header">{getSubscriptionTierIcon(getChatUserSubscriptionTier(chat.user))}</span>}
            </div>
            <div className="chat-user-status">{chat.user.isOnline ? t('online') : t('offline', 'Offline')}</div>
          </div>
        </div>
        <div className="chat-window-controls">
          {chat.unreadCount > 0 && chat.isMinimized && (
            <span className="unread-badge">{chat.unreadCount}</span>
          )}
          <button onClick={onMinimize} className="control-btn">
            {chat.isMinimized ? '▲' : '▼'}
          </button>
          <button onClick={onClose} className="control-btn close">
            ✕
          </button>
        </div>
      </div>

      {!chat.isMinimized && (
        <div className="chat-window-content">
          <div className="messages-container">
            {isLoadingHistory && (
              <div className="message" style={{ alignSelf: 'center', opacity: 0.6 }}>
                <div className="message-content" style={{ background: 'transparent', color: '#888' }}>
                  {t('loadingHistory', 'Loading history...')}
                </div>
              </div>
            )}
            {messages.map(msg => (
              <div
                key={msg.id}
                className={`message ${msg.isOwn ? 'own-message' : 'other-message'} ${msg.isOwn ? getOwnMessageGlowClass() : ''}`}
              >

                <div className="message-content">
                  {msg.text}
                </div>
                <div className="message-time">
                  {formatTime(msg.timestamp)}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={handleSendMessage} className="message-input-form">
            <input
              ref={inputRef}
              type="text"
              value={message}
              onChange={handleTyping}
              placeholder={`${t('messageUser')} ${chat.user.name}...`}
              className="message-input"
              maxLength={500}
            />
            <button type="submit" className="send-btn">
              ➤
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default LiveChat;
